import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import GrantContent from '../../../components/GrantContent'
import { listSearchSx, rowActionSx, rowDangerActionSx } from '../../../components/list-styles'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import * as Permisos from '../../../utils/Permisos'
import { formatCount } from '../common/currency-common'
import { useCurrencyListModel } from './currency-list-model'

const SKELETON_ROWS = [1, 2, 3, 4]
const columns = [
    { label: 'Moneda', className: 'min-w-0 md:min-w-[240px]' },
    { label: 'Tipo de cambio', className: 'hidden whitespace-nowrap text-right md:table-cell' },
    { label: 'Formato', className: 'hidden whitespace-nowrap md:table-cell' },
    { label: 'Acciones', className: 'hidden whitespace-nowrap text-right md:table-cell', sr: true },
]

const dialogBtnClass = 'inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40'

const CurrencyList = () => {
    const model = useCurrencyListModel()

    const tableHead = (
        <thead>
            <tr className="text-left text-xs font-bold uppercase tracking-[0.05em] text-k-muted">
                {columns.map((column) => (
                    <th
                        key={column.label}
                        className={`sticky top-0 z-10 bg-k-surface px-2.5 py-3 font-bold ${column.className} ${column.label === 'Moneda' ? 'pl-4' : ''} ${column.sr ? 'pr-4 text-right' : ''}`}
                    >
                        {column.sr ? <span className="sr-only">{column.label}</span> : column.label}
                    </th>
                ))}
            </tr>
        </thead>
    )

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Monedas">
            <Dialog open={model.deleteOpen} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar moneda</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la moneda {model.deleteLabel}? Esta acción es irreversible.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={`${dialogBtnClass} border border-k-line bg-k-surface text-k-ink hover:bg-k-hover`} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={`${dialogBtnClass} border-0 bg-k-danger font-bold !text-white hover:brightness-110`} onClick={model.confirmDelete} disabled={model.deleting}>
                        {model.deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </DialogActions>
            </Dialog>

            <div className="flex min-h-0 flex-1 flex-col gap-[18px] bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {model.loading && !model.initialLoadDone && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <div>
                            <div className="mb-2 h-7 w-32 animate-pulse rounded bg-k-line md:h-8 md:w-40" />
                            <div className="hidden h-4 w-64 animate-pulse rounded bg-k-line md:block" />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="shrink-0 border-b border-k-line px-4 py-3.5">
                                <div className="h-[42px] max-w-[380px] animate-pulse rounded-xl bg-k-line" />
                            </div>
                            <table className="w-full table-fixed border-collapse md:min-w-[640px] md:table-auto">
                                {tableHead}
                                <tbody className="bg-k-surface">
                                    {SKELETON_ROWS.map((row) => (
                                        <tr key={row} className="border-t border-k-line">
                                            <td className="px-4 py-2.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-[34px] w-[46px] animate-pulse rounded-[10px] bg-k-line" />
                                                    <div className="h-3.5 w-36 animate-pulse rounded bg-k-line" />
                                                </div>
                                            </td>
                                            <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="ml-auto h-3.5 w-20 animate-pulse rounded bg-k-line" /></td>
                                            <td className="hidden px-2.5 py-2.5 md:table-cell"><div className="h-3.5 w-20 animate-pulse rounded bg-k-line" /></td>
                                            <td className="hidden px-4 py-2.5 text-right md:table-cell"><div className="ml-auto h-9 w-40 animate-pulse rounded-[10px] bg-k-line" /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {model.error && !model.loading && (
                    <div className="mb-6 rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="text-sm text-k-danger-ink">{model.error}</p>
                        </div>
                        <Button variant="contained" onClick={model.reload} className="mt-3 !bg-k-danger !text-white">
                            Reintentar
                        </Button>
                    </div>
                )}

                {model.initialLoadDone && !model.error && (
                    <div className="flex min-h-0 flex-1 flex-col gap-[18px]">
                        <header className="flex shrink-0 flex-wrap items-center justify-between gap-3.5 md:items-end">
                            <div className="flex flex-col gap-1">
                                <h1 className="m-0 text-xl font-extrabold tracking-[-0.03em] text-k-ink md:text-[28px]">Monedas</h1>
                                <p className="m-0 hidden text-sm text-k-muted md:block">Monedas que usas en tus pólizas y su tipo de cambio · {formatCount(model.totalItems)} registros</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl border border-k-line bg-k-surface text-sm font-semibold text-k-ink hover:bg-k-hover md:w-auto md:px-4"
                                    onClick={model.reload}
                                >
                                    <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                    <span className="sr-only md:not-sr-only">Actualizar</span>
                                </button>
                                <GrantContent permisos={[Permisos.MONEDA_W]}>
                                    <Link
                                        to={model.newLocation.pathname}
                                        state={model.newLocation.state}
                                        aria-label="Nueva moneda"
                                        title="Nueva moneda"
                                        className="inline-flex h-11 w-11 items-center justify-center gap-2 rounded-xl bg-k-primary text-sm font-bold !text-white no-underline hover:brightness-105 hover:!text-white md:w-auto md:px-[18px]"
                                    >
                                        <AddIcon style={{ fontSize: 16 }} />
                                        <span className="sr-only md:not-sr-only">Nueva moneda</span>
                                    </Link>
                                </GrantContent>
                            </div>
                        </header>

                        {model.message && (
                            <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                                <ErrorOutlineIcon style={{ fontSize: 20 }} />
                                <p className="m-0 flex-1">{model.message.message}</p>
                                <button type="button" aria-label="Cerrar aviso" className="inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-inherit hover:bg-black/5" onClick={model.dismissMessage}>
                                    <CloseIcon style={{ fontSize: 16 }} />
                                </button>
                            </div>
                        )}

                        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface">
                            <div className="flex shrink-0 items-start gap-2.5 border-b border-k-line px-4 py-3.5">
                                <div className="min-w-0 flex-1">
                                    <TextField
                                        id="filterMonedasQ"
                                        label="Moneda o país"
                                        variant="outlined"
                                        size="small"
                                        value={model.search}
                                        slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                        sx={listSearchSx}
                                        onChange={(event) => model.changeSearch(event.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    title="Limpiar filtros"
                                    aria-label="Limpiar filtros"
                                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-start rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                                    onClick={model.clearSearch}
                                >
                                    <CloseIcon style={{ fontSize: 18 }} />
                                </button>
                            </div>

                            <div className={`min-h-0 flex-1 overflow-auto ${model.loading ? 'opacity-60' : ''}`}>
                                <table className="w-full table-fixed border-collapse text-sm md:min-w-[640px] md:table-auto">
                                    {tableHead}
                                    <tbody className="bg-k-surface">
                                        {model.rows.length === 0 && (
                                            <tr>
                                                <td colSpan={columns.length} className="px-4 py-12 text-center text-sm text-k-muted">
                                                    {model.search ? 'No se encontraron monedas con la búsqueda aplicada.' : (
                                                        <span className="inline-flex flex-col items-center gap-3">
                                                            Aún no configuras monedas. Agrega las que usas en tus pólizas con su tipo de cambio.
                                                            <GrantContent permisos={[Permisos.MONEDA_W]}>
                                                                <Link
                                                                    to={model.newLocation.pathname}
                                                                    state={model.newLocation.state}
                                                                    className="inline-flex h-10 items-center gap-2 rounded-xl bg-k-primary px-4 text-sm font-bold !text-white no-underline hover:brightness-105"
                                                                >
                                                                    <AddIcon style={{ fontSize: 16 }} />
                                                                    Agregar moneda
                                                                </Link>
                                                            </GrantContent>
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                        {model.rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                className="cursor-pointer border-t border-k-line transition-colors hover:bg-k-hover"
                                                onClick={() => model.editCurrency(row.id)}
                                            >
                                                <td className="max-w-[320px] px-4 py-2.5">
                                                    <div className="flex min-w-0 items-center gap-3 text-left">
                                                        <span className="flex h-[34px] min-w-[46px] shrink-0 items-center justify-center rounded-[10px] bg-tone-teal px-1.5 text-xs font-extrabold tracking-[0.04em] text-tone-teal-ink">
                                                            {row.code}
                                                        </span>
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate font-bold text-k-ink">{row.name || row.code}</span>
                                                            {row.country && <span className="truncate text-xs text-k-ink-2">{row.country}</span>}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 text-right font-semibold tabular-nums text-k-ink md:table-cell">{row.rate}</td>
                                                <td className="hidden whitespace-nowrap px-2.5 py-2.5 md:table-cell">
                                                    <span className="rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold tabular-nums text-k-accent-ink">{row.format}</span>
                                                </td>
                                                <td className="hidden whitespace-nowrap px-4 py-2.5 text-right md:table-cell">
                                                    <div className="inline-flex justify-end gap-2">
                                                        <GrantContent permisos={[Permisos.MONEDA_W]}>
                                                            <Button
                                                                variant="outlined"
                                                                aria-label={`Editar ${row.code}`}
                                                                sx={rowActionSx}
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    model.editCurrency(row.id)
                                                                }}
                                                            >
                                                                Editar
                                                            </Button>
                                                        </GrantContent>
                                                        <GrantContent permisos={[Permisos.MONEDA_D]}>
                                                            <Button
                                                                variant="outlined"
                                                                aria-label={`Eliminar ${row.code}`}
                                                                sx={rowDangerActionSx}
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    model.askDelete(row.id)
                                                                }}
                                                            >
                                                                Eliminar
                                                            </Button>
                                                        </GrantContent>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-k-line bg-k-surface px-4 py-3 text-sm text-k-muted">
                                <span>Mostrando {formatCount(model.rows.length)} de {formatCount(model.totalItems)} monedas</span>
                            </div>
                        </section>
                    </div>
                )}
            </div>
        </KarteraAdminTemplate>
    )
}

export default CurrencyList
